<script setup>
// NOTICE: KAPTION-INTEGRATION - whatsapp-flows.TEMPLATES.1: the extra fields of
// a FLOW button in the template builder. Picks a flow from the inbox's library
// (published ones; drafts/deprecated/blocked are listed disabled with the
// reason) and the screen it opens on, defaulting to the flow's first screen.
// Emits `change` with the fields to merge into the button: kaption_flow_id,
// flow_id (Meta, for the preview), flow_name and navigate_screen.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import KaptionWhatsappFlowsAPI from 'dashboard/api/kaptionWhatsappFlows';
import {
  flowScreenIds,
  flowTemplateUnavailableReason,
} from 'dashboard/helper/kaptionWhatsappFlows';

const props = defineProps({
  button: { type: Object, required: true },
  flows: { type: Array, default: () => [] },
  inboxId: { type: [Number, String], default: null },
  isLoading: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const emit = defineEmits(['change']);

const { t } = useI18n();

const screens = ref([]);

const options = computed(() =>
  props.flows.map(flow => {
    const reason = flowTemplateUnavailableReason(flow.status);
    return {
      id: flow.id,
      disabled: !!reason,
      label: reason
        ? `${flow.name} (${t(`WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FLOW_REASON_${reason}`)})`
        : flow.name,
    };
  })
);

const selectedFlow = computed(
  () =>
    props.flows.find(
      f => String(f.id) === String(props.button.kaption_flow_id)
    ) || null
);

const loadScreens = async flow => {
  screens.value = [];
  if (!flow || !props.inboxId) return;
  try {
    const { data } = await KaptionWhatsappFlowsAPI.show(props.inboxId, flow.id);
    // Templates open the published version.
    const ids = flowScreenIds(data?.published_flow_json || data?.flow_json);
    screens.value = ids;
    if (!ids.includes(props.button.navigate_screen)) {
      emit('change', { navigate_screen: ids[0] || '' });
    }
  } catch {
    screens.value = [];
  }
};

const onSelectFlow = event => {
  const flow = props.flows.find(
    f => String(f.id) === String(event.target.value)
  );
  emit('change', {
    kaption_flow_id: flow?.id || null,
    flow_id: flow?.meta_flow_id || '',
    flow_name: flow?.name || '',
    navigate_screen: '',
  });
  loadScreens(flow);
};

watch(
  () => [props.inboxId, selectedFlow.value?.id],
  () => {
    if (selectedFlow.value && !screens.value.length) {
      loadScreens(selectedFlow.value);
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="space-y-2" data-testid="kaption-template-flow-fields">
    <p v-if="isLoading" class="text-[11px] text-n-slate-11">
      {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FLOW_LOADING') }}
    </p>
    <p v-else-if="!flows.length" class="text-[11px] text-n-slate-11">
      {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FLOW_NONE') }}
    </p>
    <div v-else>
      <label class="block mb-1 text-[11px] text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FLOW_LABEL') }}
      </label>
      <select
        :value="button.kaption_flow_id || ''"
        class="w-full h-8 px-2 text-xs rounded cursor-pointer bg-n-background text-n-slate-12 outline outline-1 outline-n-weak"
        data-testid="kaption-template-flow-select"
        @change="onSelectFlow"
      >
        <option value="" disabled>
          {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FLOW_PLACEHOLDER') }}
        </option>
        <option
          v-for="option in options"
          :key="option.id"
          :value="option.id"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>
    </div>
    <div v-if="selectedFlow && screens.length">
      <label class="block mb-1 text-[11px] text-n-slate-11">
        {{ t('WHATSAPP_TEMPLATE_MGMT.CREATE.FORM.FLOW_SCREEN_LABEL') }}
      </label>
      <select
        :value="button.navigate_screen"
        class="w-full h-8 px-2 text-xs rounded cursor-pointer bg-n-background text-n-slate-12 outline outline-1 outline-n-weak"
        data-testid="kaption-template-flow-screen"
        @change="emit('change', { navigate_screen: $event.target.value })"
      >
        <option v-for="screen in screens" :key="screen" :value="screen">
          {{ screen }}
        </option>
      </select>
    </div>
    <p v-if="error" class="text-[11px] text-n-ruby-11 mt-0.5">
      {{ error }}
    </p>
  </div>
</template>
